import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FAQStyles } from '../styles/FAQStyles';

const FAQ = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const faqData = [
    {
      id: 'general-1',
      category: 'General',
      question:
        'What is Prep Qi and how does it help with board exam preparation?',
      answer:
        'Prep Qi is a comprehensive board exam preparation platform designed specifically for acupuncture and Oriental medicine students. We provide practice quizzes, final exams, question banks, and study materials organized by organ systems to help you master the content and pass your board exams with confidence.',
    },
    {
      id: 'general-2',
      category: 'General',
      question: 'Which board exams does Prep Qi cover?',
      answer:
        'Prep Qi covers the National Certification Commission for Acupuncture and Oriental Medicine (NCCAOM) board exams, including Foundations of Oriental Medicine, Acupuncture with Point Location, Biomedicine, and Chinese Herbology. Our content is regularly updated to align with the latest exam requirements.',
    },
    {
      id: 'general-3',
      category: 'General',
      question: 'How is Prep Qi different from other study materials?',
      answer:
        'Prep Qi offers a unique combination of interactive quizzes, comprehensive question banks, and personalized study tracking. Our platform is designed by someone who has successfully passed the board exams and understands the specific challenges students face. We focus on practical application and real exam scenarios.',
    },
    {
      id: 'account-1',
      category: 'Account & Subscription',
      question: 'How do I create an account?',
      answer:
        'Creating an account is simple! Click the "Sign Up" button on our homepage, enter your email address, create a secure password, and verify your email. You can also sign up using your Google account for faster registration.',
    },
    {
      id: 'account-2',
      category: 'Account & Subscription',
      question: 'What subscription plans are available?',
      answer:
        'We offer flexible subscription plans to fit your study timeline. Visit our Pricing page to see current plans, which include monthly and annual options with different levels of access to our question banks and practice exams.',
    },
    {
      id: 'account-3',
      category: 'Account & Subscription',
      question: 'Can I cancel my subscription anytime?',
      answer:
        "Yes, you can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period. We also offer a 30-day money-back guarantee if you're not satisfied with our service.",
    },
    {
      id: 'study-1',
      category: 'Study Features',
      question: 'How do the practice quizzes work?',
      answer:
        'Our practice quizzes are organized by organ systems and difficulty levels. You can take quizzes on specific topics, track your progress, and review detailed explanations for each question. The system adapts to your performance and suggests areas for improvement.',
    },
    {
      id: 'study-2',
      category: 'Study Features',
      question: 'What are final exams and how do they differ from quizzes?',
      answer:
        'Final exams are comprehensive assessments that simulate the actual board exam experience. They include questions from all organ systems and are timed to match real exam conditions. These help you gauge your readiness and identify areas needing more study.',
    },
    {
      id: 'study-3',
      category: 'Study Features',
      question: 'Can I bookmark questions for later review?',
      answer:
        'Absolutely! You can bookmark any question you want to review later. Access your bookmarked questions from the "My Bookmarks" section in your dashboard for focused study sessions.',
    },
    {
      id: 'study-4',
      category: 'Study Features',
      question: 'How do I track my progress?',
      answer:
        "Your dashboard provides detailed progress tracking including quiz scores, completion rates, and performance trends. You can see which organ systems you've mastered and which areas need more attention.",
    },
    {
      id: 'technical-1',
      category: 'Technical Support',
      question: 'What devices and browsers are supported?',
      answer:
        'Prep Qi works on all modern devices including desktop computers, tablets, and smartphones. We support Chrome, Firefox, Safari, and Edge browsers. For the best experience, we recommend using the latest version of your preferred browser.',
    },
    {
      id: 'technical-2',
      category: 'Technical Support',
      question: 'What if I experience technical issues?',
      answer:
        'If you encounter any technical problems, please contact our support team through the Contact page. We typically respond within 24-48 hours and will work to resolve any issues quickly.',
    },
    {
      id: 'technical-3',
      category: 'Technical Support',
      question: 'Can I access Prep Qi offline?',
      answer:
        "Currently, Prep Qi requires an internet connection to access all features. However, you can download certain study materials for offline review. We're working on expanding offline capabilities in future updates.",
    },
    {
      id: 'payment-1',
      category: 'Payment & Billing',
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit cards (Visa, MasterCard, American Express, Discover) and PayPal. All payments are processed securely through Stripe, ensuring your financial information is protected.',
    },
    {
      id: 'payment-2',
      category: 'Payment & Billing',
      question: 'Do you offer student discounts?',
      answer:
        'Yes! We offer special pricing for students. Contact us through the Contact page with your student ID or enrollment verification to learn about available discounts.',
    },
    {
      id: 'payment-3',
      category: 'Payment & Billing',
      question: 'How do I update my payment information?',
      answer:
        'You can update your payment information anytime from your account settings. Go to your profile, select "Billing," and follow the prompts to update your payment method.',
    },
    {
      id: 'support-1',
      category: 'Support & Contact',
      question: 'Do you offer private tutoring or one-on-one support?',
      answer:
        'Yes! I personally offer private tutoring sessions to help students with their study plans and answer specific questions. Contact me through the Contact page to discuss your needs and schedule a session.',
    },
    {
      id: 'support-2',
      category: 'Support & Contact',
      question: 'How quickly will I receive a response to my inquiry?',
      answer:
        'We typically respond to all inquiries within 24-48 hours during business days. For urgent technical issues, we prioritize faster response times.',
    },
    {
      id: 'support-3',
      category: 'Support & Contact',
      question: 'Can I suggest new features or report errors?',
      answer:
        'Absolutely! We welcome feedback and suggestions. Use the Contact page to share your ideas or report any errors you encounter. Your input helps us improve the platform for all students.',
    },
    {
      id: 'refund-1',
      category: 'Refunds & Returns',
      question: 'How do I request a refund?',
      answer:
        "To request a refund, contact our support team through the Contact page with your account email and reason for the refund request. We'll need your account email address and subscription date to process your refund. You'll receive an email confirmation once processed, and refunds typically appear in your account within 5-7 business days.",
    },
    {
      id: 'refund-2',
      category: 'Refunds & Returns',
      question: 'What items are not eligible for refunds?',
      answer:
        'The following items are not eligible for refunds: Private tutoring sessions (once scheduled or completed), custom study plans created specifically for individual students, and completed final exam attempts that have been scored.',
    },
    {
      id: 'refund-3',
      category: 'Refunds & Returns',
      question: 'Do you offer refunds for special circumstances?',
      answer:
        'Yes, we may consider refunds beyond our standard policy for special circumstances including documented medical emergencies, family emergencies, persistent technical problems that prevent platform access, and official postponement of board exams by NCCAOM. These are evaluated on a case-by-case basis and may require documentation.',
    },
  ];

  const categories = Array.from(new Set(faqData.map((item) => item.category)));

  return (
    <Box sx={FAQStyles.root}>
      {/* Header Section */}
      <Box sx={FAQStyles.headerSection}>
        <Container maxWidth="lg">
          <Typography sx={FAQStyles.title}>FAQ & Return Policy</Typography>
          <Typography sx={FAQStyles.subtitle}>
            Find answers to common questions about Prep Qi, board exam
            preparation, and our return policy. Can't find what you're looking
            for? Contact us for personalized help.
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={FAQStyles.mainContent}>
        {categories.map((category) => (
          <Box key={category} sx={FAQStyles.categorySection}>
            <Typography sx={FAQStyles.categoryTitle}>{category}</Typography>
            <Paper sx={FAQStyles.accordionContainer}>
              {faqData
                .filter((item) => item.category === category)
                .map((item) => (
                  <Accordion
                    key={item.id}
                    expanded={expanded === item.id}
                    onChange={handleChange(item.id)}
                    sx={FAQStyles.accordion}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={FAQStyles.accordionSummary}
                    >
                      <Typography sx={FAQStyles.question}>
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={FAQStyles.accordionDetails}>
                      <Typography sx={FAQStyles.answer}>
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Paper>
          </Box>
        ))}

        {/* Contact CTA */}
        <Box sx={FAQStyles.contactCTA}>
          <Typography sx={FAQStyles.contactTitle}>
            Still have questions?
          </Typography>
          <Typography sx={FAQStyles.contactSubtitle}>
            Our support team is here to help you succeed on your board exam
            journey. Whether you have questions about our platform, need help
            with refunds, or want to discuss special circumstances, we're here
            to assist you.
          </Typography>
          <Box sx={FAQStyles.contactButtons}>
            <Typography sx={FAQStyles.contactText}>
              Visit our{' '}
              <a
                href="/contact"
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                Contact page
              </a>{' '}
              to get in touch with us.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ;
